/**
 * Live read/write layer for the estimating suite's Materials & Dies catalog —
 * the shared `mfx_estimating_suite/catalog` doc:
 *   { materials: Material[], dies: Die[], coatings: string[], adhesives: string[] }
 * Same whole-doc set() model as the CSR app, so both apps stay in sync.
 * Also exposes a read hook for the `quotes` doc (win-rate / pipeline insights).
 */
import { useEffect, useState } from 'react';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from './firebase';

const SUITE = 'mfx_estimating_suite';

export interface Material { name: string; liner?: string; adhesive?: string; msi?: number; [k: string]: string | number | undefined; }
export interface Die {
  die: string; shape?: string; blank?: string;
  across?: number; around?: number; repeat?: number; nAcross?: number; nAround?: number;
  gapAround?: number; gapAcross?: number; radius?: number; notes?: string;
  [k: string]: string | number | undefined;
}
export interface Catalog { materials: Material[]; dies: Die[]; coatings: string[]; adhesives: string[]; }

const EMPTY: Catalog = { materials: [], dies: [], coatings: [], adhesives: [] };

export interface CatalogState { catalog: Catalog; loading: boolean; live: boolean; error: string | null; }

export function useCatalog(): CatalogState {
  const [state, setState] = useState<CatalogState>({ catalog: EMPTY, loading: true, live: false, error: null });
  useEffect(() => {
    const unsub = onSnapshot(doc(db, SUITE, 'catalog'),
      (snap) => {
        const d = (snap.exists() ? snap.data() : {}) as Partial<Catalog>;
        setState({
          catalog: {
            materials: Array.isArray(d.materials) ? d.materials : [],
            dies: Array.isArray(d.dies) ? d.dies : [],
            coatings: Array.isArray(d.coatings) ? d.coatings : [],
            adhesives: Array.isArray(d.adhesives) ? d.adhesives : [],
          },
          loading: false, live: true, error: null,
        });
      },
      (e) => setState((s) => ({ ...s, loading: false, live: false, error: e?.message || 'Could not load catalog' })));
    return unsub;
  }, []);
  return state;
}

export async function saveCatalog(c: Catalog): Promise<void> {
  await setDoc(doc(db, SUITE, 'catalog'), c);
}

// ── quotes doc (read-only here; used for customer win-rate / pipeline insights) ──
export interface SuiteQuote {
  id: string; quoteNo?: string; client?: string; product?: string; mode?: string;
  value?: number; qtys?: number[]; status?: string; assignee?: string;
  date?: string; updated?: string; po?: string;
  [k: string]: unknown;
}
export function useSuiteQuotes(): { quotes: SuiteQuote[]; live: boolean } {
  const [quotes, setQuotes] = useState<SuiteQuote[]>([]);
  const [live, setLive] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, SUITE, 'quotes'),
      (snap) => { const d = (snap.exists() ? snap.data() : {}) as { items?: SuiteQuote[] }; setQuotes(Array.isArray(d.items) ? d.items : []); setLive(true); },
      () => setLive(false));
    return unsub;
  }, []);
  return { quotes, live };
}

/** Persist the whole quotes array back to the shared doc (same model as the CSR app). */
export async function saveSuiteQuotes(items: SuiteQuote[]): Promise<void> {
  await setDoc(doc(db, SUITE, 'quotes'), { items });
}

/** Reserve the next Q-#### id and bump meta.qSeq (mirrors the CSR app's qSeq counter). */
export async function nextQuoteId(): Promise<string> {
  const m = await getDoc(doc(db, SUITE, 'meta'));
  const cur = ((m.exists() ? (m.data() as { qSeq?: number }).qSeq : 0) || 1);
  await setDoc(doc(db, SUITE, 'meta'), { qSeq: cur + 1 }, { merge: true });
  return 'Q-' + String(cur).padStart(4, '0');
}
